import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CirclesGameSession,
  type CirclesMilestoneInfo,
  type CirclesSessionStatus,
  buildPerfectCirclesReplayInputs,
} from '../../game/circles/circlesSession';
import type { ReplayOutcome } from '@shared/replay';
import { getBuiltinCirclesMapBySlug } from '@shared/circlesBuiltinMaps';
import { type CirclesChart } from '@shared/circlesChart';
import { chartDisplayBpm, modeLabel, stripVariantSuffix } from '@shared/chart';
import { songPageMeta } from '@shared/seo/seoMeta';
import { useDocumentHead } from '../../seo/useDocumentHead';
import { formatCirclesCredits } from '@shared/circlesCredit';
import { useAuth } from '../../auth/AuthProvider';
import { useProgression } from '../../auth/ProgressionProvider';
import { useAnalytics } from '../../analytics/AnalyticsProvider';
import { computeProgressTier } from '@shared/analytics';
import '../../components/trophyStrip.css';
import '../play.css';
import {
  fetchMapDetail,
  fetchPersonalBest,
  recordPlay,
  submitRun,
  type LeaderboardEntry,
  type MapDetail,
  type PersonalBest,
} from '../../apiClient/maps';
import { useRunToken } from '../../hooks/useRunToken';
import { subscribeVolumeChanges } from '../../game/audio/volumePrefs';
import { preloadAudioFile } from '../../game/audio/bufferCache';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { isCirclesSliderTutorialUnseen } from '../../game/tutorialFlag';
import { markSongCompleted } from '../../game/songProgress';
import { isCompetitiveMap, type MapProgression } from '@shared/progression';
import { LoopFinaleOverlay } from '../play/LoopFinaleOverlay';
import { LoopSpeedupBanner } from '../play/LoopSpeedupBanner';
import { useLoopSpeedupBanner } from '../play/useLoopSpeedupBanner';
import { ResultsOverlay } from '../play/ResultsOverlay';
import { StartOverlay } from '../play/StartOverlay';
import { PlayLoadingOverlay } from '../play/PlayLoadingOverlay';
import { usePlayShortcuts } from '../play/usePlayShortcuts';
import { PauseOverlay } from '../play/PauseOverlay';
import { PlayHeaderActions } from '../play/PlayHeaderActions';
import { LeaderboardPanel } from '../play/LeaderboardPanel';
import { friendlySubmitError } from '../play/playMessages';

function isReplayModeAllowed(): boolean {
  if (import.meta.env.DEV) {
    return true;
  }
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('gd-test-uid')) {
    return true;
  }
  return false;
}

/**
 * Circles-mode equivalent of {@link import('../PlayPage.js').PlayPage}.
 * Mirrors the lifecycle, hotkeys, overlays, and submit flow exactly so
 * a player who knows Pulse can play Circles without learning a new UX —
 * the only change is the game canvas content.
 */
export function CirclesPlayPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const replayParam = searchParams.get('replay');
  const replayMode = replayParam && isReplayModeAllowed() ? replayParam : null;
  const { user } = useAuth();
  const analytics = useAnalytics();
  const { requestToken, getToken } = useRunToken();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<CirclesGameSession | null>(null);
  const startInFlightRef = useRef<boolean>(false);
  // See {@link PlayPage}: refs let the abandon path compute progress
  // and duration synchronously inside cleanup / beforeunload without
  // waiting for a React state flush.
  const runStartedAtRef = useRef<number | null>(null);
  const liveProgressionRef = useRef<{ stars: number; cups: number }>({ stars: 0, cups: 0 });
  const [chart, setChart] = useState<CirclesChart | null>(null);
  const [mapDetail, setMapDetail] = useState<MapDetail | null>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [status, setStatus] = useState<CirclesSessionStatus>('idle');
  const [outcome, setOutcome] = useState<ReplayOutcome | null>(null);
  const [needsTap, setNeedsTap] = useState(true);
  const [submitState, setSubmitState] = useState<
    'idle' | 'submitting' | 'submitted' | 'unranked' | 'skipped' | 'error'
  >('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [personalBest, setPersonalBest] = useState<PersonalBest | null>(null);
  const [offlineMode, setOfflineMode] = useState(false);
  const [leaderboardCollapsed, setLeaderboardCollapsed] = useState(false);
  const [loopFinale, setLoopFinale] = useState<
    { stage: 'splash' | 'mode_card' } | null
  >(null);
  const [runProgression, setRunProgression] = useState<{ stars: number; cups: number }>({
    stars: 0,
    cups: 0,
  });
  const [loopsCleared, setLoopsCleared] = useState(0);
  const [resultsPreviousProgression, setResultsPreviousProgression] =
    useState<MapProgression | null>(null);
  const { forMap, applyLocalBest } = useProgression();

  useEffect(() => {
    if (!slug) {
      return;
    }
    if (sessionRef.current) {
      void sessionRef.current.destroy();
      sessionRef.current = null;
    }
    setNeedsTap(true);
    setOutcome(null);
    setStatus('idle');
    setSubmitState('idle');
    setSubmitError(null);
    setMapDetail(null);
    setChart(null);
    setChartError(null);
    setLeaderboard([]);
    setPersonalBest(null);
    setRunProgression({ stars: 0, cups: 0 });
    setResultsPreviousProgression(null);
    setLoopFinale(null);
    setLoopsCleared(0);
    saveModePref('circles');

    let cancelled = false;
    const load = async () => {
      try {
        const detail = await fetchMapDetail(slug, { mode: 'circles' });
        if (cancelled) {
          return;
        }
        setMapDetail(detail);
        if (detail.chart.mode !== 'circles') {
          setChartError(
            `Map "${detail.chart.slug}" is a ${detail.chart.mode}-mode chart and cannot be played here.`,
          );
          return;
        }
        setChart(detail.chart);
        setLeaderboard(detail.leaderboard);
        setOfflineMode(false);
        fetchPersonalBest(detail.id)
          .then((pb) => {
            if (!cancelled) {
              setPersonalBest(pb);
            }
          })
          .catch((err) => {
            console.warn('Failed to load personal best:', err);
          });
      } catch (err) {
        if (cancelled) {
          return;
        }
        const builtin = getBuiltinCirclesMapBySlug(slug);
        if (builtin) {
          console.warn('Server map fetch failed, falling back to built-in chart:', err);
          setChart(builtin.chart);
          setOfflineMode(true);
          return;
        }
        setChartError(err instanceof Error ? err.message : 'Failed to load map');
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // First-slider onboarding. The moment a slider-bearing chart finishes
  // loading we divert a player who hasn't seen the slider lesson into it,
  // carrying the current slug as `?next=` so they land straight back here
  // once they're done. Content-gated, not variant-gated: the slider-free
  // `normal` tier has no sliders so it never triggers, while any
  // slider-bearing tier (hard / extreme / insane, or a future chart) does.
  // Skipped in replay mode so perfect-replay and test harnesses run the
  // real chart unimpeded.
  //
  // `useLayoutEffect` (not `useEffect`) so the redirect commits before the
  // browser paints — otherwise the player sees a one-frame flash of this
  // play page's Start overlay before the route swaps to the tutorial.
  useLayoutEffect(() => {
    if (!slug || !chart || replayMode) {
      return;
    }
    const hasSlider = chart.hitObjects.some((h) => h.kind === 'slider');
    if (hasSlider && isCirclesSliderTutorialUnseen()) {
      navigate(modePath('circles', `tutorial/slider?next=${encodeURIComponent(slug)}`), {
        replace: true,
      });
    }
  }, [slug, chart, replayMode, navigate]);

  const onMilestone = useCallback((info: CirclesMilestoneInfo) => {
    setRunProgression({
      stars: info.starsEarned,
      cups: info.cupsEarned,
    });
    liveProgressionRef.current = {
      stars: info.starsEarned,
      cups: info.cupsEarned,
    };
    if (info.pause) {
      setLoopFinale({ stage: 'splash' });
    }
  }, []);

  const onComplete = useCallback(
    async (o: ReplayOutcome) => {
      setOutcome(o);
      // A cleared run (any mode) graduates the player out of the
      // heaviest in-game teaching hints. Replays aren't the player's
      // own live clear, so they don't count.
      if (o.completed && !replayMode) {
        markSongCompleted();
      }
      const earned = sessionRef.current?.getProgressionEarnedThisRun() ?? {
        stars: 0,
        cups: 0,
      };
      setRunProgression(earned);
      const loopsClearedCount = sessionRef.current?.getLoopsCleared() ?? 0;
      setLoopsCleared(loopsClearedCount);

      if (mapDetail && runStartedAtRef.current !== null) {
        const durationMs = Math.max(0, Math.round(performance.now() - runStartedAtRef.current));
        analytics.emit({
          eventType: 'song_end',
          mode: 'circles',
          mapId: mapDetail.id,
          mapSlug: mapDetail.slug,
          difficulty: chart?.difficulty,
          variant: chart?.variant,
          completed: o.completed,
          progressTier: computeProgressTier(earned.stars, earned.cups),
          durationMs,
          props: {
            score: o.score,
            accuracy: o.accuracy,
            perfects: o.perfects,
            goods: o.goods,
            oks: o.oks ?? 0,
            misses: o.misses,
            max_combo: o.maxCombo,
            death_beat: o.deathBeat,
            death_reason: o.deathReason ?? null,
            stars: earned.stars,
            cups: earned.cups,
            loops_cleared: loopsClearedCount,
            offline_mode: offlineMode,
            replay_mode: replayMode,
          },
        });
      }
      runStartedAtRef.current = null;
      liveProgressionRef.current = { stars: 0, cups: 0 };
      const beforeBest = mapDetail
        ? forMap(mapDetail.slug, 'circles')
        : ({ mapSlug: slug ?? '', mode: 'circles', competitiveEligible: false, stars: 0, cups: 0 } satisfies MapProgression);
      setResultsPreviousProgression(beforeBest);
      if (!sessionRef.current) {
        return;
      }
      if (!mapDetail) {
        setSubmitState('error');
        setSubmitError(
          offlineMode
            ? "Server unreachable - couldn't save this run. Please reload to retry."
            : "Couldn't save this run.",
        );
        return;
      }
      setSubmitState('submitting');
      setSubmitError(null);
      try {
        const replay = sessionRef.current.getCapturedReplay();
        const result = await submitRun({
          mapId: mapDetail.id,
          outcome: o,
          replay,
          progression: earned,
          timing: sessionRef.current.getTimingReport(),
          runToken: getToken(),
        });
        if (result.skipped === 'no_inputs') {
          setSubmitState('skipped');
        } else if (result.progressionSaved === false) {
          setSubmitState('error');
          setSubmitError(
            "Score saved, but we couldn't update your stars/cups. Retry to save your progress.",
          );
        } else {
          setSubmitState(result.serverValidated ? 'submitted' : 'unranked');
          try {
            await recordPlay(mapDetail.id);
          } catch (err) {
            console.warn('Failed to record play count:', err);
          }
        }
        if (result.progression) {
          applyLocalBest({
            mapSlug: mapDetail.slug,
            mode: 'circles',
            competitiveEligible: result.progression.competitiveEligible,
            stars: result.progression.stars,
            cups: result.progression.cups,
          });
        }
        try {
          const refreshed = await fetchMapDetail(mapDetail.slug, { mode: 'circles' });
          setLeaderboard(refreshed.leaderboard);
          setMapDetail(refreshed);
        } catch (err) {
          console.warn('Failed to refresh leaderboard:', err);
        }
      } catch (err) {
        console.error('Failed to submit run:', err);
        setSubmitState('error');
        setSubmitError(friendlySubmitError(err));
      }
    },
    [mapDetail, offlineMode, applyLocalBest, forMap, slug, analytics, chart, replayMode, getToken],
  );

  const startSession = useCallback(async () => {
    if (!chart || !canvasRef.current) {
      return;
    }
    if (startInFlightRef.current) {
      return;
    }
    startInFlightRef.current = true;
    setNeedsTap(false);
    setOutcome(null);
    setSubmitState('idle');
    setSubmitError(null);
    setLoopFinale(null);
    setRunProgression({ stars: 0, cups: 0 });
    setResultsPreviousProgression(null);
    setLoopsCleared(0);

    try {
      if (sessionRef.current) {
        // Mid-run restart should close the previous song_start in
        // analytics — see PlayPage.tsx for the rationale.
        emitAbandonRef.current();
        await sessionRef.current.destroy();
        sessionRef.current = null;
      }
      const enableLoops = replayMode === null;
      const session = new CirclesGameSession({
        chart,
        canvas: canvasRef.current,
        audioOffsetMs: user?.audioOffsetMs ?? 0,
        replayInputs:
          replayMode === 'perfect' ? buildPerfectCirclesReplayInputs(chart) : undefined,
        onStatusChange: (s) => setStatus(s),
        onComplete: (o) => void onComplete(o),
        onMilestone,
        enableLoops,
        testHook: replayMode !== null,
      });
      sessionRef.current = session;
      try {
        await session.start();
        runStartedAtRef.current = performance.now();
        liveProgressionRef.current = { stars: 0, cups: 0 };
        if (mapDetail) {
          requestToken(mapDetail.id);
          analytics.emit({
            eventType: 'song_start',
            mode: 'circles',
            mapId: mapDetail.id,
            mapSlug: mapDetail.slug,
            difficulty: chart.difficulty,
            variant: chart.variant,
            props: {
              offline_mode: offlineMode,
              bpm: chart.bpm,
              replay_mode: replayMode,
            },
          });
        }
      } catch (err) {
        console.error('Failed to start Circles game session:', err);
        sessionRef.current = null;
        void session.destroy();
        setNeedsTap(true);
        setSubmitState('error');
        setSubmitError(
          "Couldn't start audio. Try clicking Start again or check your browser's audio permissions.",
        );
      }
    } finally {
      startInFlightRef.current = false;
    }
  }, [chart, mapDetail, offlineMode, replayMode, user?.audioOffsetMs, onComplete, onMilestone, analytics, requestToken]);

  useEffect(() => {
    if (!needsTap || !chart) {
      return;
    }
    if (replayMode) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === ' ' ||
        e.code === 'Space' ||
        e.key === 'Enter' ||
        e.code === 'KeyZ' ||
        e.code === 'KeyX'
      ) {
        e.preventDefault();
        void startSession();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [needsTap, chart, replayMode, startSession]);

  usePlayShortcuts({
    status,
    hasOutcome: outcome !== null,
    onPause: () => sessionRef.current?.manualPause(),
    onResume: () => void sessionRef.current?.resume(),
    onRestart: () => void startSession(),
  });

  // Mid-run live volume: the TopNav speaker popover publishes through the
  // shared pub/sub in volumePrefs.ts. We forward each change into the live
  // audio engine so dragging the slider takes effect inside the same frame.
  useEffect(() => {
    return subscribeVolumeChanges(() => {
      sessionRef.current?.applyVolumePrefs();
    });
  }, []);

  // See {@link PlayPage.tsx}: ref-pattern so the abandon emit fires
  // exactly once on real unmount, with the latest closure data, not
  // on every callback-dep flip.
  const emitAbandonRef = useRef(() => {});
  emitAbandonRef.current = () => {
    if (runStartedAtRef.current === null) {
      return;
    }
    if (!mapDetail) {
      runStartedAtRef.current = null;
      return;
    }
    const durationMs = Math.max(0, Math.round(performance.now() - runStartedAtRef.current));
    const earned = liveProgressionRef.current;
    const loopsClearedCount = sessionRef.current?.getLoopsCleared() ?? 0;
    analytics.emit({
      eventType: 'song_abandon',
      mode: 'circles',
      mapId: mapDetail.id,
      mapSlug: mapDetail.slug,
      difficulty: chart?.difficulty,
      variant: chart?.variant,
      completed: false,
      progressTier: computeProgressTier(earned.stars, earned.cups),
      durationMs,
      props: {
        stars: earned.stars,
        cups: earned.cups,
        loops_cleared: loopsClearedCount,
        offline_mode: offlineMode,
        replay_mode: replayMode,
      },
    });
    void analytics.flushAll('unload');
    runStartedAtRef.current = null;
    liveProgressionRef.current = { stars: 0, cups: 0 };
  };

  useEffect(() => {
    const onUnload = () => emitAbandonRef.current();
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
    };
  }, []);

  useEffect(() => {
    return () => {
      emitAbandonRef.current();
      if (sessionRef.current) {
        void sessionRef.current.destroy();
        sessionRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (replayMode && chart && needsTap) {
      void startSession();
    }
  }, [replayMode, chart, needsTap, startSession]);

  useEffect(() => {
    if (!loopFinale) {
      return undefined;
    }
    if (loopFinale.stage === 'splash') {
      const t = setTimeout(() => {
        setLoopFinale((cur) => (cur ? { ...cur, stage: 'mode_card' } : cur));
      }, 1800);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      void sessionRef.current?.startNextLoop();
    }, 1200);
    return () => clearTimeout(t);
  }, [loopFinale]);

  useEffect(() => {
    if (status === 'lead_in' || status === 'running') {
      setLoopFinale(null);
    }
  }, [status]);

  // Warm the HTTP cache for the track as soon as the chart is known, so the
  // post-tap AudioContext only has to decode (not fetch). Shrinks the
  // cold-start gap that otherwise shows as a black canvas on slower devices.
  useEffect(() => {
    if (chart) {
      preloadAudioFile(chart.audio.src);
    }
  }, [chart]);

  // Speed-up banner over the count-in for infinite-mode repeats (loop 3+).
  const speedupBanner = useLoopSpeedupBanner(
    status === 'lead_in',
    () => sessionRef.current?.getLoopIndex() ?? 0,
  );

  // Sync the document head to the loaded song for SPA navigation. RouteHead
  // owns the non-play routes but defers play pages to here, where the chart
  // (and so the real song title) is known. No-ops until the chart loads.
  useDocumentHead(
    chart && slug
      ? songPageMeta({
          mode: 'circles',
          modeLabel: modeLabel('circles'),
          slug,
          songTitle: stripVariantSuffix(chart),
          songDescription: chart.description,
          bpm: Math.round(chartDisplayBpm(chart.bpm)),
        })
      : null,
  );

  if (chartError) {
    return (
      <div className="play-error">
        <h2>Map not found</h2>
        <p>{chartError}</p>
        <Link to={modePath('circles')} className="btn">
          Back to Circles
        </Link>
      </div>
    );
  }

  if (!chart) {
    return (
      <div className="loading-screen">
        <p className="muted">Loading {slug}...</p>
      </div>
    );
  }

  // Required-circle count (excludes spinners which auto-resolve and
  // shouldn't dilute the "how dense is this map?" signal).
  const requiredCircles = chart.hitObjects.filter((h) => h.kind !== 'spinner').length;

  return (
    <div className="play-page" data-testid="circles-play-page">
      <div className="play-header">
        <div>
          <h2 className="play-title">
            <span className="play-title-text">{stripVariantSuffix(chart)}</span>
            {chart.variant !== 'normal' && (
              <span
                className={`variant-badge variant-badge-${chart.variant}`}
                data-testid="circles-play-variant-badge"
              >
                {chart.variant.toUpperCase()}
              </span>
            )}
          </h2>
          <p className="muted">
            {chartDisplayBpm(chart.bpm).toFixed(0)} BPM &middot; Difficulty {chart.difficulty} &middot;{' '}
            {requiredCircles} circles
            {mapDetail?.likeCount !== undefined && (
              <>
                {' '}&middot; {mapDetail.likeCount.toLocaleString()} likes &middot;{' '}
                {mapDetail.playCount.toLocaleString()} plays
              </>
            )}
          </p>
          {formatCirclesCredits(chart.audio.attribution, chart.mapper) && (
            <p
              className="muted faint play-attribution"
              data-testid="circles-audio-attribution"
            >
              {formatCirclesCredits(chart.audio.attribution, chart.mapper)}
            </p>
          )}
          {personalBest && personalBest.score > 0 && (
            <p
              className="play-pb-line muted faint"
              data-testid="circles-personal-best"
            >
              Your best: <strong>{personalBest.score.toLocaleString()}</strong> &middot;{' '}
              {(personalBest.accuracy * 100).toFixed(0)}% &middot;{' '}
              {personalBest.completed ? 'cleared' : 'incomplete'}
            </p>
          )}
        </div>
        <PlayHeaderActions
          isPlaying={status === 'running' || status === 'lead_in'}
          onPause={() => sessionRef.current?.manualPause()}
          onRestart={() => void startSession()}
          testIdPrefix="circles-play"
        />
      </div>

      {offlineMode && (
        <div className="error-box" data-testid="circles-offline-banner">
          Server unreachable. Playing offline - your run won&apos;t be saved.
        </div>
      )}

      <div className="play-layout">
        <div className="play-canvas-wrap">
          <canvas
            ref={canvasRef}
            className="play-canvas"
            data-testid="circles-game-canvas"
            width={960}
            height={540}
            onClick={() => {
              if (needsTap) {
                void startSession();
              }
            }}
          />

          {needsTap && (
            <StartOverlay
              title={chart.title}
              description={chart.description}
              attribution={
                formatCirclesCredits(chart.audio.attribution, chart.mapper) ?? undefined
              }
              instructionsHint={
                <>
                  Move your cursor to the circle. Click (or press <kbd>Z</kbd> /{' '}
                  <kbd>X</kbd>) when the approach ring meets it.
                </>
              }
              isFirstTime={false}
              firstTimeHint={null}
              onStart={() => void startSession()}
            />
          )}

          {status === 'starting' && !needsTap && (sessionRef.current?.getLoopIndex() ?? 0) === 0 && (
            <PlayLoadingOverlay audioSrc={chart.audio.src} />
          )}

          {status === 'paused' && (
            <PauseOverlay
              onRestart={() => void startSession()}
              onResume={() => void sessionRef.current?.resume()}
              hotkeys={[
                { key: 'Click', action: 'tap a circle' },
                { key: 'Z / X', action: 'tap at cursor' },
                { key: 'Esc / P', action: 'pause / resume' },
                { key: 'R', action: 'restart while paused / retry' },
              ]}
            />
          )}

          {loopFinale && <LoopFinaleOverlay stage={loopFinale.stage} />}

          {speedupBanner && (
            <LoopSpeedupBanner
              speed={speedupBanner.speed}
              message={speedupBanner.message}
            />
          )}

          {outcome && (
            <ResultsOverlay
              competitiveEligible={mapDetail !== null && isCompetitiveMap(mapDetail)}
              outcome={outcome}
              submitState={submitState}
              submitError={submitError}
              onRetry={() => void startSession()}
              previousBest={personalBest?.score ?? 0}
              earnedThisRun={runProgression}
              loopsCleared={loopsCleared}
              previousProgression={
                resultsPreviousProgression ?? {
                  mapSlug: slug ?? '',
                  mode: 'circles',
                  competitiveEligible: false,
                  stars: 0,
                  cups: 0,
                }
              }
              chart={chart}
            />
          )}

          <LeaderboardPanel
            competitiveEligible={mapDetail !== null && isCompetitiveMap(mapDetail)}
            entries={leaderboard}
            collapsed={leaderboardCollapsed}
            onToggle={() => setLeaderboardCollapsed((v) => !v)}
            selfUid={user?.uid}
          />
        </div>
      </div>
    </div>
  );
}
